/** @jsxImportSource @emotion/react */
import Pagination from 'components/atoms/Pagination'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { BillingLoading } from 'components/lab/billingEraReports/BillingLoading'
import {
  DENIAL_NO_REASON_CODE,
  denialResolutionLabel,
  DENIAL_UNASSIGNED,
  DENIAL_WORKLIST_TABS,
} from 'components/lab/billingDenial/consts'
import { DenialActionKind, useDenialActions, useLabUserOptions } from 'components/lab/billingDenial/DenialActions'
import routes from 'components/lab/constants/routes'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import { UserPermission } from 'components/lab/user/permissions'
import { formatCalendarDate } from 'helpers/dates'
import { useAppSelector } from 'hooks/redux'
import { useLabPermission } from 'hooks/useLabPermission'
import React from 'react'
import { createPortal } from 'react-dom'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import ReactSelect, { StylesConfig } from 'react-select'
import { useGetDenialReasonSummaryQuery, useGetDenialWorklistQuery } from 'store/services/billingDenials'
import { useGetLabPayersQuery } from 'store/services/payer'
import { BillingDenialDto, BillingDenialWorklistFilters, BillingDenialWorklistItem } from 'store/types'
import { billingAdjustmentCodes } from 'ts-common/constants/billingAdjustmentCodes'
import { useDebounce } from 'use-debounce'

type Status = BillingDenialDto['status']

const reasonDescriptions = new Map(billingAdjustmentCodes.map((code) => [code.code, code.description]))

const money = (value: number) => `$${value.toFixed(2)}`

const reasonLabel = (code: string) =>
  code === DENIAL_NO_REASON_CODE ? 'No reason code' : code

const ReasonCode = ({ code }: { code: string }) => {
  const description = reasonDescriptions.get(code)

  return (
    <TooltipWrapper text={description || ''} show={Boolean(description)} maxWidth={440}>
      <span css={description ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 } : undefined}>
        {reasonLabel(code)}
      </span>
    </TooltipWrapper>
  )
}

// One line; the full text on hover.
const ReasonDescription = ({ code }: { code: string }) => {
  const description = reasonDescriptions.get(code)

  if (!description) {
    return <span>-</span>
  }

  return (
    <TooltipWrapper text={description} show maxWidth={440}>
      <div css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</div>
    </TooltipWrapper>
  )
}

// The reason a Pending list is opened on, with the way back to all reasons.
const ReasonHeader = ({ code, onBack }: { code: string; onBack: () => void }) => (
  <div
    className="d-flex align-items-center mb-2"
    css={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 4, gap: 12, padding: '6px 12px' }}>
    <Button variant="link" size="sm" className="p-0" css={{ whiteSpace: 'nowrap' }} onClick={onBack}>
      <i className="fas fa-arrow-left mr-1" />
      All reasons
    </Button>
    <span css={{ borderLeft: '1px solid #dee2e6', alignSelf: 'stretch' }} />
    <span className="badge badge-secondary" css={{ fontSize: 13, whiteSpace: 'nowrap' }}>
      {reasonLabel(code)}
    </span>
    {reasonDescriptions.has(code) && (
      <div css={{ fontWeight: 600, minWidth: 0 }}>
        <ReasonDescription code={code} />
      </div>
    )}
  </div>
)

type TextFilters = Pick<BillingDenialWorklistFilters, 'reasonCode' | 'traceNumber' | 'procedure'>
type DateFilters = Pick<BillingDenialWorklistFilters, 'denialDateFrom' | 'denialDateTo' | 'dosFrom' | 'dosTo'>

const DATE_FILTERS: { key: keyof DateFilters; label: string }[] = [
  { key: 'denialDateFrom', label: 'Denied from' },
  { key: 'denialDateTo', label: 'Denied to' },
  { key: 'dosFrom', label: 'DOS from' },
  { key: 'dosTo', label: 'DOS to' },
]

// matchedName: the other name a payer search matched, for the "via" note.
type Choice<T> = { value: T; label: string; matchedName?: string | null }

// Selects sized like the inputs beside them.
const compactSelectStyles = <T,>(): StylesConfig<Choice<T>, false> => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 'calc(1.5em + 0.75rem + 2px)' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 8px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 8px' }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
})

const Filters = ({
  text,
  dates,
  payer,
  owner,
  onText,
  onDates,
  onPayer,
  onOwner,
}: {
  text: TextFilters
  dates: DateFilters
  payer: Choice<number> | null
  owner: Choice<string> | null
  onText: (value: TextFilters) => void
  onDates: (value: DateFilters) => void
  onPayer: (value: Choice<number> | null) => void
  onOwner: (value: Choice<string> | null) => void
}) => {
  const currentUserId = useAppSelector((state) => state.init.id)
  const [ownerSearch, setOwnerSearch] = React.useState('')
  const users = useLabUserOptions(ownerSearch)
  const ownerOptions: Choice<string>[] = [
    ...(currentUserId ? [{ value: String(currentUserId), label: 'Assigned to me' }] : []),
    { value: DENIAL_UNASSIGNED, label: 'Unassigned' },
    ...users.map((user) => ({ value: String(user.value), label: user.label })),
  ]
  const [payerSearch, setPayerSearch] = React.useState('')
  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)
  const { data: payers } = useGetLabPayersQuery(
    { name: debouncedPayerSearch, active: true },
    { skip: debouncedPayerSearch.length < 2 },
  )

  const textInput = (key: keyof TextFilters, label: string, placeholder: string) => (
    <div className="col-6 col-md-2">
      <label className="textGray mb-1">{label}</label>
      <input
        className="form-control"
        placeholder={placeholder}
        value={text[key] ?? ''}
        onChange={(event) => onText({ ...text, [key]: event.target.value })}
      />
    </div>
  )

  return (
    <div className="row mb-2">
      {textInput('reasonCode', 'Reason', 'CARC, e.g. 197')}
      <div className="col-12 col-md-3">
        <label className="textGray mb-1">Plan</label>
        <ReactSelect
          isClearable
          isSearchable
          placeholder="Search payers…"
          value={payer}
          inputValue={payerSearch}
          onInputChange={(value) => setPayerSearch(value)}
          onChange={(item) => onPayer(item ? { value: Number(item.value), label: item.label } : null)}
          options={(payers ?? []).map((item) => ({
            value: Number(item.payerId),
            label: `${item.name} · #${item.payerId}`,
            matchedName: item.matchedName,
          }))}
          formatOptionLabel={formatPayerOptionLabel}
          filterOption={filterPayerOption}
          noOptionsMessage={() => (payerSearch.length < 2 ? 'Type at least 2 letters' : 'No payers found')}
          menuPortalTarget={document.body}
          styles={compactSelectStyles<number>()}
        />
      </div>
      {textInput('traceNumber', 'Check / trace number', 'Check or EFT number')}
      {textInput('procedure', 'Procedure', 'CPT, e.g. 95810')}
      <div className="col-12 col-md-3">
        <label className="textGray mb-1">Assigned to</label>
        <ReactSelect
          isClearable
          isSearchable
          placeholder="Anyone"
          value={owner}
          inputValue={ownerSearch}
          onInputChange={(value) => setOwnerSearch(value)}
          onChange={(item) => onOwner(item ? { value: item.value, label: item.label } : null)}
          options={ownerOptions}
          noOptionsMessage={() => 'No users found'}
          menuPortalTarget={document.body}
          styles={compactSelectStyles<string>()}
        />
      </div>
      <div className="w-100" />
      {DATE_FILTERS.map(({ key, label }) => (
        <div key={key} className="col-6 col-md-2 mt-2">
          <label className="textGray mb-1">{label}</label>
          <input
            type="date"
            className="form-control"
            value={dates[key] ?? ''}
            onChange={(event) => onDates({ ...dates, [key]: event.target.value })}
          />
        </div>
      ))}
    </div>
  )
}

const ReasonSummary = ({
  filters,
  onOpen,
}: {
  filters: BillingDenialWorklistFilters
  onOpen: (reasonCode: string) => void
}) => {
  const { data, isLoading, isFetching } = useGetDenialReasonSummaryQuery(filters)

  if (isLoading) {
    return <BillingLoading />
  }

  return (
    <div className="table-responsive" css={{ opacity: isFetching ? 0.6 : 1 }}>
      <table className="table table-sm table-hover mb-0">
        <thead>
          <tr>
            <th>Reason</th>
            <th>Description</th>
            <th>Procedures</th>
            <th>Claims</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {!data?.length ? (
            <tr>
              <td colSpan={5} className="text-center p-4">
                No pending denials
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={row.reasonCode}
                data-testid="denial-reason-row"
                css={{ cursor: 'pointer' }}
                onClick={() => onOpen(row.reasonCode)}>
                <td css={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{reasonLabel(row.reasonCode)}</td>
                <td css={{ maxWidth: 0, width: '100%' }}>
                  <ReasonDescription code={row.reasonCode} />
                </td>
                <td>{row.procedures}</td>
                <td>{row.denials}</td>
                <td>{money(row.amount)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

const ACTIONS: { kind: DenialActionKind; label: string; pendingOnly?: boolean }[] = [
  { kind: 'APPEAL', label: 'Appeal' },
  { kind: 'RESUBMIT', label: 'Resubmit' },
  { kind: 'IN_PROCESS', label: 'Move to In Process', pendingOnly: true },
  { kind: 'WRITE_OFF', label: 'Write Off' },
  { kind: 'PATIENT', label: 'Push to Patient' },
]

const chargeLink = (item: BillingDenialWorklistItem) =>
  `${routes.index}${routes.patient.root}/${item.patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${item.chargeId}`

const DenialList = ({
  status,
  filters,
  header,
  actionsSlot,
}: {
  status: Status
  filters: BillingDenialWorklistFilters
  header?: React.ReactNode
  // Where the bulk actions render: the tab row.
  actionsSlot: HTMLElement | null
}) => {
  const canAct = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(25)
  const [selected, setSelected] = React.useState<string[]>([])
  const { data, isLoading, isFetching } = useGetDenialWorklistQuery({ ...filters, status, page, size: pageSize })
  const { run, modals } = useDenialActions({ onDone: () => setSelected([]) })
  const selectable = canAct && status !== 'RESOLVED'
  const items: BillingDenialWorklistItem[] = data?.items ?? []
  const filterKey = JSON.stringify(filters)

  React.useEffect(() => {
    setPage(0)
  }, [filterKey])

  React.useEffect(() => {
    setSelected([])
  }, [filterKey, page, pageSize])

  if (isLoading) {
    return <BillingLoading />
  }

  const selectedOwned = items.filter((item) => item.ownerUserId && selected.includes(item.id)).map((item) => item.id)
  // Preselected only when every selected denial suggests the same reason.
  const selectedReasons = [
    ...new Set(items.filter((item) => selected.includes(item.id)).map((item) => item.suggestedWriteOffReason)),
  ]
  const sharedSuggestedReason = selectedReasons.length === 1 ? selectedReasons[0] : null
  const allSelected = items.length > 0 && items.every((item) => selected.includes(item.id))
  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))

  return (
    <div>
      {selectable && actionsSlot && createPortal(
        <div
          className="d-flex align-items-center justify-content-end flex-wrap"
          css={{ gap: 5, '& .btn': { paddingLeft: 8, paddingRight: 8 } }}>
          {ACTIONS.filter((action) => !action.pendingOnly || status === 'PENDING').map((action) => (
            <Button
              key={action.kind}
              variant="outline-primary"
              disabled={!selected.length}
              onClick={() => run(action.kind, selected, sharedSuggestedReason)}>
              {action.label}
            </Button>
          ))}
          <span css={{ borderLeft: '1px solid #dee2e6', alignSelf: 'stretch', margin: '0 4px' }} />
          <Button variant="outline-secondary" disabled={!selected.length} onClick={() => run('ASSIGN_TO_ME', selected)}>
            Assign to me
          </Button>
          <Button variant="outline-secondary" disabled={!selected.length} onClick={() => run('ASSIGN', selected)}>
            Assign
          </Button>
          <Button
           
            variant="outline-secondary"
            disabled={!selectedOwned.length}
            onClick={() => run('UNASSIGN', selectedOwned)}>
            Unassign
          </Button>
        </div>,
        actionsSlot,
      )}
      {header}
      <div className="table-responsive" css={{ opacity: isFetching ? 0.6 : 1 }}>
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              {selectable && (
                <th>
                  <div className="d-flex align-items-center" css={{ gap: 6 }}>
                    <input
                      type="checkbox"
                      className="m-0"
                      aria-label="Select all"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? [] : items.map((item) => item.id))}
                    />
                    {!!selected.length && (
                      <span className="text-muted" title={`${selected.length} selected`} css={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
                        {selected.length}
                      </span>
                    )}
                  </div>
                </th>
              )}
              <th>Patient</th>
              <th>DOS</th>
              <th>Procedure</th>
              <th>Plan</th>
              <th>Payer claim #</th>
              <th>Denial date</th>
              <th>Reason</th>
              <th>Amount</th>
              <th>Assigned to</th>
              {status === 'RESOLVED' && <th>Resolution</th>}
            </tr>
          </thead>
          <tbody>
            {!items.length ? (
              <tr>
                <td colSpan={11} className="text-center p-4">
                  No denials found
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} data-testid="denial-row">
                  {selectable && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select denial for ${item.patientName || 'patient'}`}
                        checked={selected.includes(item.id)}
                        onChange={() => toggle(item.id)}
                      />
                    </td>
                  )}
                  <td>
                    <Link to={chargeLink(item)}>{item.patientName || `Patient ${item.patientId}`}</Link>
                    {item.claimNumber && <div css={{ fontSize: 11, color: '#6c757d' }}>{item.claimNumber}</div>}
                  </td>
                  <td>{formatCalendarDate(item.dateOfService)}</td>
                  <td>{item.procedures.join(', ')}</td>
                  <td>
                    {item.payerName || '-'}
                    {item.traceNumber && <div css={{ fontSize: 11, color: '#6c757d' }}>Check/EFT {item.traceNumber}</div>}
                  </td>
                  <td>{item.payerClaimNumber || '-'}</td>
                  <td>{formatCalendarDate(item.denialDate)}</td>
                  <td>
                    {item.reasonCodes.map((code, index) => (
                      <span key={code}>
                        {index > 0 && ', '}
                        <ReasonCode code={code} />
                      </span>
                    ))}
                  </td>
                  <td>{money(item.amount)}</td>
                  <td>{item.ownerName || <span className="text-muted">Unassigned</span>}</td>
                  {status === 'RESOLVED' && (
                    <td>{item.resolution ? denialResolutionLabel(item.resolution, item.rebilledPayerName) : '-'}</td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        className="pagination-bar"
        currentPage={page}
        totalCount={data?.totalItems || 1}
        pageSize={pageSize}
        siblingCount={1}
        setPageSize={setPageSize}
        onPageChange={setPage}
      />
      {modals}
    </div>
  )
}

/**
 * Denials by status. Pending opens on its reasons with what each holds;
 * picking a reason (or typing one) lists its claims.
 */
export const DenialsWorklist = () => {
  const [actionsSlot, setActionsSlot] = React.useState<HTMLDivElement | null>(null)
  const [status, setStatus] = React.useState<Status>('PENDING')
  const [text, setText] = React.useState<TextFilters>({})
  const [dates, setDates] = React.useState<DateFilters>({})
  const [payer, setPayer] = React.useState<Choice<number> | null>(null)
  const [owner, setOwner] = React.useState<Choice<string> | null>(null)
  const [debouncedText] = useDebounce(text, 300)

  const filters = React.useMemo<BillingDenialWorklistFilters>(() => {
    const trimmed = Object.fromEntries(
      Object.entries({ ...debouncedText, ...dates })
        .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        .filter(([, value]) => value),
    )

    return {
      ...trimmed,
      ...(payer ? { payerId: payer.value } : {}),
      ...(owner ? { ownerUserId: owner.value } : {}),
    }
  }, [debouncedText, dates, payer, owner])

  const showReasons = status === 'PENDING' && !filters.reasonCode

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3" css={{ gap: 8 }}>
        {DENIAL_WORKLIST_TABS.map((tab) => (
          <Button
            key={tab.status}
            variant={status === tab.status ? 'dark' : 'outline-dark'}
            onClick={() => setStatus(tab.status)}>
            {tab.label}
          </Button>
        ))}
        <div ref={setActionsSlot} className="ml-auto" />
      </div>
      <Filters
        text={text}
        dates={dates}
        payer={payer}
        owner={owner}
        onText={setText}
        onDates={setDates}
        onPayer={setPayer}
        onOwner={setOwner}
      />
      {showReasons ? (
        <ReasonSummary filters={filters} onOpen={(reasonCode) => setText({ ...text, reasonCode })} />
      ) : (
        <DenialList
          key={status}
          status={status}
          filters={filters}
          actionsSlot={actionsSlot}
          header={
            status === 'PENDING' && filters.reasonCode ? (
              <ReasonHeader code={filters.reasonCode} onBack={() => setText({ ...text, reasonCode: '' })} />
            ) : null
          }
        />
      )}
    </div>
  )
}
